import React from 'react';
import {useTranslation} from 'react-i18next';
import {ShareButton, PublishButton} from './RoomHeader';

// Status / word / timer bar rendered above the canvas in guess mode.
// Renders one of four states: choosing, active round, intermission, idle.
export default function GuessStatusBar(props) {
    const {t} = useTranslation();
    const {
        currentUser, homeButton, room,
        guessRound, guessIntermission, everyoneDraws, myVote,
        isMobile, isMobileLandscape, isSpectator,
        onSkip, onPublish, publishPending,
    } = props;

    const intermissionActive = !!(guessIntermission && guessIntermission.active);
    const isPeriodEnd = intermissionActive && guessIntermission.isPeriodEnd;

    const padding = (isMobile || isSpectator) && !isMobileLandscape
        ? '2px 4px'
        : isMobileLandscape ? '2px 4px 2px 4px' : '6px 8px';

    return (
        <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: isMobile ? 4 : 12, padding,
            minHeight: isMobileLandscape ? 38 : isMobile ? 38 : 42, boxSizing: 'border-box',
            flexWrap: 'nowrap',
            background: intermissionActive
                ? (isPeriodEnd ? '#3a3520' : '#1a4a2a')
                : '#2a2a3e',
            color: '#eee', fontSize: isMobileLandscape ? 12 : isMobile ? 13 : 15, fontFamily: 'monospace',
            borderBottom: intermissionActive
                ? (isPeriodEnd ? '2px solid #c9a820' : '2px solid #3d9e5f')
                : '2px solid #444',
            transition: 'background 0.4s, border-color 0.4s',
        }}>
            <span style={{flexShrink: 0}}>{homeButton}</span>

            <StatusContent
                t={t}
                currentUser={currentUser}
                guessRound={guessRound}
                guessIntermission={guessIntermission}
                everyoneDraws={everyoneDraws}
                myVote={myVote}
                onSkip={onSkip}
            />

            <div style={{display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0}}>
                {/* PublishButton intentionally not rendered in guess mode in original code. */}
                {/* (kept here as a no-op for parity with the previous nested block) */}
                {false && <PublishButton onPublish={onPublish} publishPending={publishPending} compact/>}
                <ShareButton room={room}/>
            </div>
        </div>
    );
}

function StatusContent({t, currentUser, guessRound, guessIntermission, everyoneDraws, myVote, onSkip}) {
    if (guessRound && guessRound.choosing) {
        return (
            <span style={{fontSize: 14, color: '#aab'}}>
                {t('drawing.choosingWord', {drawer: guessRound.drawer})}
            </span>
        );
    }

    if (guessRound && guessRound.active) {
        const word = guessRound.word;
        const pattern = guessRound.wordPattern;
        return (
            <div style={{
                display: 'flex', flexDirection: 'row', alignItems: 'center',
                gap: 8, flex: '1 1 0', justifyContent: 'center', minWidth: 0,
            }}>
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.1}}>
                    <span data-word-pattern={pattern || ''} style={{
                        fontSize: 18, fontWeight: 'bold', letterSpacing: 3,
                        color: word ? '#ffd666' : '#eee',
                    }}>
                        {word || pattern}
                    </span>
                    {word && pattern && (
                        <span style={{fontSize: 11, letterSpacing: 2, color: '#888', fontFamily: 'monospace'}}>
                            {pattern}
                        </span>
                    )}
                </div>
                <span style={{
                    fontSize: 13, fontWeight: 'bold', minWidth: 40, textAlign: 'center',
                    color: guessRound.timeLeft <= 10 ? '#ff6b6b' : '#7c83ff',
                }}>
                    {guessRound.timeLeft}s
                </span>
                {guessRound.drawer === currentUser && (
                    <button
                        onClick={onSkip}
                        style={{
                            fontSize: 12, padding: '3px 10px', cursor: 'pointer',
                            background: '#664444', color: '#faa', border: 'none', borderRadius: 5,
                            fontWeight: 'bold', flexShrink: 0,
                        }}
                    >
                        {t('drawing.skip')}
                    </button>
                )}
            </div>
        );
    }

    if (guessIntermission && guessIntermission.active) {
        const isPeriodEnd = guessIntermission.isPeriodEnd;
        const headlineText = isPeriodEnd
            ? guessIntermission.message
            : guessIntermission.winner
                ? t('drawing.guessedIt', {winner: guessIntermission.winner})
                : !guessIntermission.drawer
                    ? t('drawing.skipped')
                    : t('drawing.timesUp');
        const showVoteHint = guessIntermission.drawer
            && guessIntermission.drawer !== currentUser
            && !isPeriodEnd;
        return (
            <>
                <div style={{textAlign: 'left', flexShrink: 1, minWidth: 0}}>
                    <div style={{
                        fontSize: 14, fontWeight: 'bold',
                        color: isPeriodEnd ? '#ffd666' : '#6eff9e',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                        {headlineText}
                    </div>
                    {guessIntermission.nextDrawer && (
                        <div style={{fontSize: 11, color: '#7ecf99'}}>
                            {t('drawing.nextDrawer', {drawer: guessIntermission.nextDrawer})}
                        </div>
                    )}
                </div>
            </>
        );
    }

    return (
        <div style={{display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', justifyContent: 'center'}}>
            <span style={{color: '#888'}}>
                {guessIntermission && guessIntermission.message
                    ? guessIntermission.message
                    : everyoneDraws
                        ? t('drawing.waitingEveryoneDraws')
                        : t('drawing.waitingToStart')}
            </span>
        </div>
    );
}
